// Binnenkomende links en serverparameters (§7, §8).
import { kalenderdag } from './datum.js';
import { filterbaar, sorteerbaar, vindKolom } from './kolom.js';
import type { Context, FilterStand, Geweigerd, Kolom, Lijst, Sortering } from './typen.js';

const LEEG = '_leeg';

export interface KolomParameter {
  parameter: string;
  sleutel: string;
  rol: 'waarde' | 'van' | 'tot';
}

/** Parameternamen van de filterbare kolommen, met `voorvoegsel`. */
export function kolomParameters<R, C extends Context>(lijst: Lijst<R, C>): KolomParameter[] {
  const pre = lijst.voorvoegsel ?? '';
  return lijst.kolommen.filter(filterbaar).flatMap((k): KolomParameter[] =>
    k.type === 'datum'
      ? [
          { parameter: pre + (k.urlVan ?? `${k.sleutel}Van`), sleutel: k.sleutel, rol: 'van' },
          { parameter: pre + (k.urlTot ?? `${k.sleutel}Tot`), sleutel: k.sleutel, rol: 'tot' },
        ]
      : [{ parameter: pre + (k.urlNaam ?? k.sleutel), sleutel: k.sleutel, rol: 'waarde' }],
  );
}

export function sorteerNaarParameter(sortering: Sortering): string {
  return sortering.map((s) => (s.richting === 'af' ? '-' : '') + s.sleutel).join(',');
}

/** `a,-b` → sortering; `null` bij een onbekende, niet-sorteerbare of dubbele kolom, of te veel kolommen. */
export function sorteerUitParameter<R, C extends Context>(waarde: string, lijst: Lijst<R, C>): Sortering | null {
  if (waarde.trim() === '') return [];
  const uit: Sortering = [];
  for (const deel of waarde.split(',').map((d) => d.trim())) {
    const af = deel.startsWith('-');
    const sleutel = af ? deel.slice(1) : deel;
    const k = vindKolom(lijst, sleutel);
    if (!k || !sorteerbaar(k) || uit.some((s) => s.sleutel === sleutel)) return null;
    uit.push({ sleutel, richting: af ? 'af' : 'op' });
  }
  return uit.length <= (lijst.maxSorteerkolommen ?? 5) ? uit : null;
}

export interface Startfilter {
  filters: FilterStand;
  sortering: Sortering;
  pagina?: number;
  paginagrootte?: number;
  /** Parameters die deze lijst heeft gelezen (en die uit het adres mogen). */
  gebruikt: string[];
  geweigerd: Geweigerd[];
}

function keuzes<R, C extends Context>(k: Kolom<R, C>, ruw: string): (string | null)[] | null {
  const delen = [...new Set(ruw.split(',').map((d) => d.trim()).filter((d) => d !== ''))].map((d) => (d === LEEG ? null : d));
  if (delen.length === 0 || (k.enkelvoudig && delen.length > 1)) return null;
  if (k.vasteWaarden && delen.some((d) => d !== null && !k.vasteWaarden!.includes(d))) return null;
  return delen;
}

/** Binnenkomende link → lijststand. Ongeldige waarden worden niet toegepast maar gemeld; overige parameters blijven gelden. */
/** Alles met `get`, zoals `URLSearchParams` (de package kent geen DOM-typen). */
export interface Parameters {
  get(naam: string): string | null;
}

export function leesStartfilter<R, C extends Context>(params: Parameters, lijst: Lijst<R, C>): Startfilter {
  const pre = lijst.voorvoegsel ?? '';
  const uit: Startfilter = { filters: {}, sortering: [], gebruikt: [], geweigerd: [] };
  const lees = (parameter: string) => {
    const w = params.get(parameter);
    if (w !== null) uit.gebruikt.push(parameter);
    return w;
  };

  for (const { parameter, sleutel, rol } of kolomParameters(lijst)) {
    const ruw = lees(parameter);
    if (ruw === null) continue;
    const k = vindKolom(lijst, sleutel)!;
    const weiger = () => uit.geweigerd.push({ parameter, waarde: ruw, sleutel });
    if (k.type === 'keuzelijst') {
      const w = keuzes(k, ruw);
      if (w) uit.filters[sleutel] = w;
      else weiger();
    } else if (k.type === 'datum') {
      const dag = kalenderdag(ruw);
      if (dag === null) weiger();
      else uit.filters[sleutel] = { ...(uit.filters[sleutel] as object | undefined), [rol]: dag };
    } else {
      uit.filters[sleutel] = ruw;
    }
  }
  if (lijst.alleenKolomfilters) return uit;

  const sort = lees(pre + 'sort');
  const sortBy = sort === null ? lees(pre + 'sortBy') : null;
  if (sort !== null) {
    const s = lijst.sorteerAliassen?.[sort] ?? sorteerUitParameter(sort, lijst);
    if (s) uit.sortering = s.map((x) => ({ ...x }));
    else uit.geweigerd.push({ parameter: pre + 'sort', waarde: sort });
  } else if (sortBy !== null) {
    const richting = lees(pre + 'sortDirection')?.toLowerCase() ?? null;
    const eerste = vindKolom(lijst, sortBy)?.eersteRichting ?? 'op';
    const af = richting === null ? eerste === 'af' : richting === 'desc' ? true : richting === 'asc' ? false : null;
    const s = af === null ? null : sorteerUitParameter((af ? '-' : '') + sortBy, lijst);
    if (s) uit.sortering = s;
    else uit.geweigerd.push({ parameter: pre + 'sortBy', waarde: `${sortBy} ${richting ?? ''}`.trim() });
  }

  for (const [parameter, veld] of [
    [pre + 'page', 'pagina'],
    [pre + 'pageSize', 'paginagrootte'],
  ] as const) {
    const ruw = lees(parameter);
    if (ruw === null) continue;
    if (/^\d+$/.test(ruw) && Number(ruw) > 0) uit[veld] = Number(ruw);
    else uit.geweigerd.push({ parameter, waarde: ruw });
  }
  return uit;
}

/**
 * Lijststand → parameters, zodat `leesStartfilter` exact dezelfde stand teruggeeft. Lege filters vallen weg.
 * ponytail: een keuzewaarde met een komma overleeft de rondreis niet; escapen zodra zo'n waarde bestaat.
 */
export function standNaarParameters<R, C extends Context>(lijst: Lijst<R, C>, filters: FilterStand, sortering: Sortering): Record<string, string> {
  const uit: Record<string, string> = {};
  for (const { parameter, sleutel, rol } of kolomParameters(lijst)) {
    const f = filters[sleutel];
    if (f === undefined) continue;
    if (Array.isArray(f)) {
      if (f.length > 0) uit[parameter] = f.map((w) => w ?? LEEG).join(',');
    } else if (typeof f === 'string') {
      if (f.trim() !== '') uit[parameter] = f;
    } else if (rol !== 'waarde' && f[rol]) {
      uit[parameter] = f[rol]!;
    }
  }
  if (sortering.length > 0) uit[(lijst.voorvoegsel ?? '') + 'sort'] = sorteerNaarParameter(sortering);
  return uit;
}
